import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import {
  normalizeCatalogEmbedStatus,
  resolveRenderedLabel,
} from "../catalog-embeds/data-catalog-entry-shared";
import { DataCatalogTemplateEntryNodeView } from "../catalog-embeds/data-catalog-template-entry-node-view";
import type { DataCatalogEntryNodeOptions } from "./data-catalog-entry-node";

function parseJsonStringArray(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

function serializeStringArray(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const normalized = value.filter((item): item is string => typeof item === "string");
  return normalized.length > 0 ? JSON.stringify(normalized) : null;
}

export const DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME = "dataCatalogTemplateEntry";

export const DataCatalogTemplateEntryNode = Node.create<DataCatalogEntryNodeOptions>({
  name: DATA_CATALOG_TEMPLATE_ENTRY_NODE_NAME,
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      onOpenDetails: undefined,
      onOpenExternal: undefined,
      onRefresh: undefined,
      onRelink: undefined,
      onRemove: undefined,
    };
  },

  addAttributes() {
    return {
      embedId: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-embed-id") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.embedId === "string" && attributes.embedId.length > 0
            ? { "data-embed-id": attributes.embedId }
            : {},
      },
      connectionId: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-connection-id") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.connectionId === "string" && attributes.connectionId.length > 0
            ? { "data-connection-id": attributes.connectionId }
            : {},
      },
      catalogDisplayName: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-catalog-display-name") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.catalogDisplayName === "string" &&
          attributes.catalogDisplayName.length > 0
            ? { "data-catalog-display-name": attributes.catalogDisplayName }
            : {},
      },
      providerKey: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-provider-key") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.providerKey === "string" && attributes.providerKey.length > 0
            ? { "data-provider-key": attributes.providerKey }
            : {},
      },
      objectStableRef: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-object-stable-ref") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.objectStableRef === "string" && attributes.objectStableRef.length > 0
            ? { "data-object-stable-ref": attributes.objectStableRef }
            : {},
      },
      objectCategory: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-object-category") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.objectCategory === "string" && attributes.objectCategory.length > 0
            ? { "data-object-category": attributes.objectCategory }
            : {},
      },
      objectDisplayLabel: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-object-display-label") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.objectDisplayLabel === "string" &&
          attributes.objectDisplayLabel.length > 0
            ? { "data-object-display-label": attributes.objectDisplayLabel }
            : {},
      },
      embedKind: {
        default: "template",
        parseHTML: () => "template",
        renderHTML: () => ({ "data-embed-kind": "template" }),
      },
      templateKey: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-template-key") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.templateKey === "string" && attributes.templateKey.length > 0
            ? { "data-template-key": attributes.templateKey }
            : {},
      },
      templateLabel: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-template-label") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.templateLabel === "string" && attributes.templateLabel.length > 0
            ? { "data-template-label": attributes.templateLabel }
            : {},
      },
      selectedFieldKeys: {
        default: [],
        parseHTML: (element) =>
          parseJsonStringArray(element.getAttribute("data-selected-field-keys")),
        renderHTML: (attributes) => {
          const serialized = serializeStringArray(attributes.selectedFieldKeys);
          return serialized ? { "data-selected-field-keys": serialized } : {};
        },
      },
      selectedFieldLabels: {
        default: [],
        parseHTML: (element) =>
          parseJsonStringArray(element.getAttribute("data-selected-field-labels")),
        renderHTML: (attributes) => {
          const serialized = serializeStringArray(attributes.selectedFieldLabels);
          return serialized ? { "data-selected-field-labels": serialized } : {};
        },
      },
      selectedFieldValues: {
        default: [],
        parseHTML: (element) =>
          parseJsonStringArray(element.getAttribute("data-selected-field-values")),
        renderHTML: (attributes) => {
          const serialized = serializeStringArray(attributes.selectedFieldValues);
          return serialized ? { "data-selected-field-values": serialized } : {};
        },
      },
      status: {
        default: "current",
        parseHTML: (element) => normalizeCatalogEmbedStatus(element.getAttribute("data-status")),
        renderHTML: (attributes) => {
          const status = normalizeCatalogEmbedStatus(attributes.status);
          return { "data-status": status };
        },
      },
      brokenReason: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-broken-reason") ?? "",
        renderHTML: (attributes) =>
          typeof attributes.brokenReason === "string" && attributes.brokenReason.length > 0
            ? { "data-broken-reason": attributes.brokenReason }
            : {},
      },
      // Semantically lastResolvedAt — only ever stamped when status resolves to "current".
      lastSyncedAt: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute("data-last-synced-at");
          if (!value) return null;
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        },
        renderHTML: (attributes) =>
          typeof attributes.lastSyncedAt === "number"
            ? { "data-last-synced-at": String(attributes.lastSyncedAt) }
            : {},
      },
      // Last refresh attempt, any outcome (LAT-571) — distinct from lastSyncedAt.
      lastCheckedAt: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute("data-last-checked-at");
          if (!value) return null;
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        },
        renderHTML: (attributes) =>
          typeof attributes.lastCheckedAt === "number"
            ? { "data-last-checked-at": String(attributes.lastCheckedAt) }
            : {},
      },
    };
  },

  parseHTML() {
    return [
      { tag: 'div[data-type="data-catalog-template-entry"]' },
      { tag: 'span[data-type="data-catalog-template-entry"]' },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "data-catalog-template-entry",
        class: "df-data-catalog-template-entry",
      }),
      resolveRenderedLabel(node.attrs),
    ];
  },

  renderText({ node }) {
    return resolveRenderedLabel(node.attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(DataCatalogTemplateEntryNodeView);
  },
});
